import type { BrandConfig } from './types';
import { DEFAULT_SURVEY_QUESTIONS } from './types';
import { BRAND_HTML_META } from './htmlMeta';
import { hypeappBrand } from './hypeapp';
import { shohinJohinBrand } from './shohin-johin';
import logoWhite from '../../assets/take8_logo_white_trans.png';
import logoBlack from '../../assets/take8_logo_black_trans.png';

/**
 * TAKE 8 ブランド設定
 *
 * Light Media が自社運用する広告向けデモサイト (SAITE と同系統)。
 * バックエンドのキーは Light Media のものを共用する「Light Media キー利用型」パートナー。
 * 詳細: docs/partner-setup-lightmedia-keys.md
 *
 * 広告向けのため UI/機能は hypeapp(SAITE) をベースにしつつ、
 * カラー(蛍光緑)・サービス名・ロゴ・ホーム画面を TAKE 8 用に上書きする。
 * ※ homePageVariant は TAKE 8 専用の写真フォワード型ホーム ('take8')。
 *   default(商品上品) や hypeapp(SAITE) のホームとは別コンポーネントを使う。
 */
export const take8Brand: BrandConfig = {
  ...hypeappBrand,
  id: 'take8',
  serviceName: BRAND_HTML_META.take8.serviceName,
  companyName: 'Light Media Inc.',
  // ログイン画面と同じ生成り紙基調 (partners.color_scheme / onboarding_color_scheme
  // も 'light')。ここが 'dark' だと partner 設定が届く前の初回描画だけ暗色になり、
  // 背景とインクの明暗が反転してちらつくため BrandConfig 側も light に揃える。
  colorScheme: 'light',
  logoWhite: logoWhite,  // ダーク背景向け (白抜き透過ロゴ)
  logoDark: logoBlack,   // ライト背景向け (黒透過ロゴ)
  favicon: BRAND_HTML_META.take8.favicon,
  colors: {
    // primary: 大面積の主要面はアンビエント寄りに落ち着かせる (深緑グレー)
    primary: '#3E5A45',
    primaryHover: '#324A39',
    primaryRgb: '62 90 69',
    // sub / accent: 蛍光緑をブランドカラーとして使用
    subSolid: '#39FF14',
    subSolidHover: '#2BE00C',
    subRgb: '57 255 20',
    accentSolid: '#39FF14',
    accentSolidHover: '#2BE00C',
    accentRgb: '57 255 20',
    // AuthGate ライト配色 (.wrapper.light) と同じ生成り紙のグラデーション。
    // 背景画像を外したときにログイン画面とそのまま地続きに見える色。
    bgGradientStart: '#F8F5F0',
    bgGradientMid: '#F6F3EE',
    bgGradientEnd: '#F2EEE7',
  },
  features: {
    ...hypeappBrand.features,
    gtm: BRAND_HTML_META.take8.gtm,
    signupEnabled: true, // hypeapp は招待制で false。TAKE 8 はログイン画面の新規登録導線を出す
    materialsNavTab: false, // MainNav の「素材」タブは hypeapp のみ (継承を打ち消す)
    amazonIntegration: true, // 案件ページの Amazon タブ + SP-API / Amazon広告連携 (hypeapp 継承の打ち消し)
    productsManualFirst: true, // タブ並びは マニュアル → Amazon (default ブランドと逆順)
    fishLicensedVoicesDefault: false, // 権利処理済みの既定オンは SAITE のみ (hypeapp 継承の打ち消し)
  },
  // 用語は default(商品上品) ベース。hypeapp ベースだと「商品」→「プロジェクト」、
  // 「シナリオ」→「絵コンテ」等に呼び替わるため labels を default のものに戻したうえで、
  // take8 では「商品」を「案件」と呼ぶため product 系のラベルのみ上書きする。
  labels: {
    ...shohinJohinBrand.labels,
    product: '案件',
    products: '案件',
    productName: '案件名',
    productDescription: '案件の説明',
    productNewDefault: '案件情報を入力してください',
    productShort: '案件',
  },
  urls: {
    website: 'https://www.take8.jp',
    contact: 'https://www.lightmedia.jp/contact',
    privacy: 'https://www.lightmedia.jp/privacy',
  },
  // hypeapp (SAITE demo.html 誘導) ではなく default(商品上品) と同じ
  // 「/auth/signup へ誘導」に上書きする (ベースが hypeappBrand のため明示が必要)。
  auth: {
    notFoundGuidance: shohinJohinBrand.auth.notFoundGuidance,
  },
  // 初回アンケートは default と同じ 利用目的/性別 の2問 (hypeappBrand 継承の打ち消し)。
  // backend の SURVEY_CONFIGS に take8 は無く default 扱いのため、SAITE 用の
  // usage_purpose/discovery_channel を継承すると /me/survey に設問が無く
  // モーダルが選択肢ゼロになり、マイページの後から回答する行も消える。
  surveyQuestions: DEFAULT_SURVEY_QUESTIONS,
  homePageVariant: 'take8',
};
